/** @jsxImportSource ../../i18n */
import React, { useId } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { usePrefersReducedMotion } from '../../theme/motion';
import { formatDate, formatMoney } from '../../theme/format';
import { MASKED_AMOUNT } from '../ui/Money';
import { ChartTooltip } from './ChartTooltip';
import { axisProps, chartColors, cursorProps, gridProps, safeId } from './chartTheme';

export const ValueAreaChart = ({ data, height = 300, dataKey = 'value', color = chartColors.gold, valueFormatter, axisFormatter, sensitive = true, className }) => {
  const id = safeId(useId());
  const { hidden } = usePrivacy();
  const reduced = usePrefersReducedMotion();
  const masked = hidden && sensitive;
  const formatValue = valueFormatter || ((v) => formatMoney(v, { decimals: 0 }));
  const formatAxis = axisFormatter || ((v) => formatMoney(v, { compact: true }));
  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={`area-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.24} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="date" {...axisProps} minTickGap={36} tickMargin={10} tickFormatter={(d) => formatDate(d, 'dayMonth')} />
          <YAxis {...axisProps} orientation="right" width={64} tickMargin={6} domain={['auto', 'auto']} tickFormatter={(v) => (masked ? '•••' : formatAxis(v))} />
          <Tooltip
            cursor={cursorProps}
            content={<ChartTooltip labelFormatter={(d) => formatDate(d, 'long')} valueFormatter={(v) => (masked ? MASKED_AMOUNT : formatValue(v))} />}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            strokeWidth={1.75}
            fill={`url(#area-${id})`}
            dot={false}
            activeDot={{ r: 4, fill: color, stroke: chartColors.canvas, strokeWidth: 2 }}
            isAnimationActive={!reduced}
            animationDuration={700}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};
